/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Row, Col } from 'antd';

import { useArticle } from '@api/hooks';
import { NO_MARGIN_BOTTOM } from '@constants';
import { Typography } from '@atoms';
import { ArticleField, ModeSelect, TextField, ColorField, PositionField, ContentModuleModal } from '@molecules';
import MediaField from '@organisms/MediaField';
import { getImage } from '@utils/media';

import BackgroundSelect from '@organisms/BackgroundSelect/index';

import Form from '../Form';

import { FORM_FIELD, MODE, MODES, INITIAL_VALUES } from './constants';
import Item from './Item';

const SingleNewsArticle = ({
  visible,
  onSubmit,
  hide,
  previewImg,
  onDelete,
  initialValues: data = INITIAL_VALUES,
  errors,
  isUltimateDesign,
}) => {
  const [form] = Form.useForm(FORM_FIELD);

  const articleQuery = useArticle(data?.articleId);
  React.useEffect(() => {
    if (visible && articleQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.ARTICLE.name]: articleQuery.data });
    }
  }, [articleQuery.article, articleQuery.data, articleQuery.status, form, visible]);

  const [mode, setMode] = React.useState();
  React.useEffect(() => {
    if (visible) {
      setMode(data?.articleId ? MODE.EXISTING.value : MODE.MANUAL.value);
    }
  }, [data, visible]);

  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleOnValuesChange = ({
    [FORM_FIELD.ARTICLE.name]: article,
    [FORM_FIELD.BACKGROUND.name]: backgroundImage,
  }) => {
    if (article && mode === MODE.EXISTING.value) {
      form.setFieldsValue({
        [FORM_FIELD.IMAGE.name]: getImage(article, 'featureImage'),
      });
    }
    if (backgroundImage) {
      form.setFieldsValue({
        [FORM_FIELD.BACKGROUND.name]: { id: backgroundImage.id, edited: backgroundImage.edited },
      });
    }
  };

  return (
    <ContentModuleModal
      visible={visible}
      title="Single news article"
      subtitle="Choose an article to feature on the page"
      okText={onDelete ? 'SAVE CHANGES' : 'ADD NEWS ARTICLE'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Form
        form={form}
        layout="vertical"
        wrapperCol={{ span: 24 }}
        initialValues={initialValues}
        onSerializedFinish={handleSubmit}
        onValuesChange={handleOnValuesChange}
      >
        <ModeSelect mode={mode} onSelect={setMode} modes={MODES} />
        {mode === MODE.MANUAL.value ? (
          <>
            {!isUltimateDesign && <MediaField {...FORM_FIELD.IMAGE} />}
            <TextField {...FORM_FIELD.TITLE} />
            <TextField {...FORM_FIELD.SUBTITLE} />
            {isUltimateDesign && <TextField {...FORM_FIELD.DESCRIPTION} />}
            <TextField {...FORM_FIELD.URL} />
            {isUltimateDesign && <TextField {...FORM_FIELD.URL_TEXT} />}
          </>
        ) : (
          <>
            <ArticleField {...FORM_FIELD.ARTICLE} isTypeFilter />
            <MediaField {...FORM_FIELD.IMAGE} buttonText="CROP IMAGE" />
            {isUltimateDesign && <TextField {...FORM_FIELD.DESCRIPTION} />}
            {isUltimateDesign && <TextField {...FORM_FIELD.URL_TEXT} />}
          </>
        )}
        <Typography.Title level={3} style={{ marginTop: 40 }}>
          Settings
        </Typography.Title>
        {!isUltimateDesign && (
          <Row align="middle" gutter={20} style={{ marginBottom: 20 }}>
            <Col flex="0 0 50%">
              <PositionField {...FORM_FIELD.TEXT_POSITION} style={NO_MARGIN_BOTTOM} />
            </Col>
            <Col flex="0 0 auto">
              <ColorField {...FORM_FIELD.TEXT_COLOR} style={NO_MARGIN_BOTTOM} />
            </Col>
          </Row>
        )}
        {isUltimateDesign && (
          <BackgroundSelect
            isImgBackground={!!data?.backgroundImageId && !!data?.mobileBackgroundImageId}
            background={FORM_FIELD.BACKGROUND}
            mobileBackground={FORM_FIELD.MOBILE_BACKGROUND}
            backgroundColours={FORM_FIELD.BACKGROUND_COLOUR}
          />
        )}
      </Form>
    </ContentModuleModal>
  );
};

SingleNewsArticle.Item = Item;

export default SingleNewsArticle;
